// frontend/src/components/notifications/NotificationDropdown.jsx
/**
 * NotificationDropdown — Atlassian-style enriched notification list.
 *
 * @description
 *   Sectioned by date (Today/Yesterday/Older), grouped by element
 *   (RMA case, PO, SKU, ops script, LSM watch). Per-group row shows
 *   latest notification + stacked-update badge for siblings. Multi-
 *   update groups expand inline (Collapse + indented rail) — they do
 *   NOT navigate. Single-item groups navigate to deep_link on click.
 *   Each child row inside an expanded group navigates to its own
 *   deep_link. Unread dot on right edge. Mute on row hover.
 *
 * @useWhen
 *   - Mounted inside Bell's Popover (max-width 440px)
 *
 * @dontUseFor
 *   - Standalone page (use NotificationCenter)
 *
 * @related
 *   - Bell, NotificationCenter, useNotificationsContext
 *   - groupNotifications.js — grouping helper
 *   - domainIcon.js — per-domain icons
 *
 * @props
 *   onClose: () => void
 */
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Stack, Typography, Divider, IconButton, Tooltip, Chip, Collapse,
} from '@mui/material'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useNotificationsContext } from '../../notifications/NotificationsContext'
import { groupAndSection } from '../../notifications/groupNotifications'
import { getDomainIcon, getUrgencyColor } from '../../notifications/domainIcon'
import SegmentedSlider from '../common/SegmentedSlider'
import { FS, SP, RAIL_WIDTH, TOUCH_TARGET_COARSE_QUADRAT, focusRing, hoverOnly } from '../../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function elementSubtitle(group_key) {
  const parts = group_key.split(':')
  if (parts[0] === 'rma' && parts[1] === 'case') return `RMA Case ${parts[2]}`
  if (parts[0] === 'po') return `PO ${parts[1]}`
  if (parts[0] === 'pricing') return `Pricing · ${parts[1]}`
  if (parts[0] === 'scraper') return `Scraper · ${parts[3] || parts[2]}`
  if (parts[0] === 'ops') return `Ops · ${parts[1].split('/').pop()}`
  if (parts[0] === 'lsm') return `LSM watch ${parts[1]}`
  return ''
}

function ChildRow({ notification, onNavigate }) {
  const isUnread = notification.seen_at == null
  const urgencyColor = getUrgencyColor(notification.urgency)
  return (
    <Box
      onClick={() => onNavigate(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(notification) } }}
      sx={{
        position: 'relative',
        pl: SP.lg, pr: SP.sm, py: 0.75,
        cursor: 'pointer',
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        borderBottom: '1px solid', borderBottomColor: 'divider',
        borderLeft: `${RAIL_WIDTH}px solid`, borderLeftColor: urgencyColor,
        ...hoverOnly({ bgcolor: 'action.selected' }),
        // Innen versetzt: die Zeile ist dicht, ein Aussenring schnitte an der
        // Nachbarzeile an (FOCUS_RING.innen).
        '&:focus-visible': focusRing({ innen: true }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{
              fontSize: FS.xs, fontWeight: isUnread ? 600 : 400,
              color: 'text.primary', flexGrow: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {notification.title}
            </Typography>
            <Typography sx={{ fontSize: FS.xxs, color: 'text.disabled', flexShrink: 0 }}>
              {timeAgo(notification.created_at)}
            </Typography>
          </Stack>
          {notification.body && (
            <Typography sx={{
              fontSize: FS.xxs, color: 'text.secondary', mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {notification.body}
            </Typography>
          )}
        </Box>
        {isUnread && (
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: urgencyColor, alignSelf: 'center', flexShrink: 0,
          }} />
        )}
      </Stack>
    </Box>
  )
}

function GroupRow({ group, onNavigate, onMute }) {
  const { latest, items, has_unread } = group
  const isMulti = items.length > 1
  const [expanded, setExpanded] = useState(false)

  const DomainIcon = getDomainIcon(latest.domain)
  const urgencyColor = getUrgencyColor(latest.urgency)
  const subtitle = elementSubtitle(group.group_key)
  const moreCount = items.length - 1

  const handleClick = () => {
    if (isMulti) setExpanded(e => !e)
    else onNavigate(latest)
  }

  return (
    <>
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-expanded={isMulti ? expanded : undefined}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick() } }}
        sx={{
          position: 'relative',
          px: SP.sm, py: SP.tight,
          cursor: 'pointer',
          bgcolor: has_unread ? 'action.hover' : 'transparent',
          borderBottom: '1px solid', borderBottomColor: 'divider',
          // 🔴 R16 C2: die Einblendung gehoert nicht hinter `hoverOnly` — Ruhelage an der
          // Zeigerfaehigkeit, keine Hover-Wirkung (`.row-actions` ist ab 600 px aus).
          // Hausform `RMACasesDashboardBody.jsx` › `.caseHoverActions` (W-52).
          '&:hover .row-actions': { opacity: 1 },
          '@media (hover: none)': { '& .row-actions': { opacity: 1 } },
          ...hoverOnly({ bgcolor: 'action.selected' }),
          // Innen versetzt: die Zeile ist dicht, ein Aussenring schnitte an der
          // Nachbarzeile an (FOCUS_RING.innen).
          '&:focus-visible': focusRing({ innen: true }),
          '&:focus-within .row-actions': { opacity: 1 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: 'background.default', border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <DomainIcon sx={{ fontSize: 18, color: urgencyColor }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
              <Typography sx={{
                fontSize: FS.sm, fontWeight: has_unread ? 700 : 500,
                color: 'text.primary', flexGrow: 1,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {latest.title}
              </Typography>
              <Typography sx={{ fontSize: FS.xxs, color: 'text.disabled', flexShrink: 0 }}>
                {timeAgo(latest.created_at)}
              </Typography>
            </Stack>
            {subtitle && (
              <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mb: 0.25 }}>
                {subtitle}
              </Typography>
            )}
            {latest.body && (
              <Typography sx={{
                fontSize: FS.xs, color: 'text.secondary', mb: 0.5,
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {latest.body}
              </Typography>
            )}
            {isMulti && (
              <Chip
                size="small"
                label={`${expanded ? 'Hide' : 'Show'} ${moreCount} more update${moreCount === 1 ? '' : 's'}`}
                icon={expanded ? <ExpandLessIcon sx={{ fontSize: 12 }} /> : <ExpandMoreIcon sx={{ fontSize: 12 }} />}
                onClick={(e) => { e.stopPropagation(); setExpanded(x => !x) }}
                sx={{
                  fontSize: FS.xxs, height: 20,
                  bgcolor: expanded ? 'primary.50' : 'action.selected',
                  color: 'primary.main',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '& .MuiChip-icon': { ml: 0.5, color: 'primary.main' },
                  ...hoverOnly({ bgcolor: 'primary.100' }),
                }}
              />
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.25}
            className="row-actions"
            sx={{
              opacity: { xs: 1, sm: 0 },
              transition: 'opacity 0.15s',
              flexShrink: 0,
            }}
          >
            <Tooltip title="Mute for 24h">
              <IconButton
                size="small"
                aria-label="Mute for 24h"
                onClick={(e) => { e.stopPropagation(); onMute(latest) }}
                // K8 (2026-10-10): der Boden war an die BREITE gebunden
                // (`{ xs: 44, sm: 'auto' }`) — auf einem Tablet (≥ 600 px,
                // grober Zeiger) fiel er damit auf `auto` und die Flaeche
                // schrumpfte. Der Hausboden ist zeigergebunden.
                sx={{ ...TOUCH_TARGET_COARSE_QUADRAT }}
              >
                <VolumeOffIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          {isMulti && (
            <Tooltip title={expanded ? 'Collapse updates' : `Show ${moreCount} more`}>
              <IconButton
                size="small"
                aria-label={expanded ? 'Collapse updates' : `Show ${moreCount} more updates`}
                aria-expanded={expanded}
                onClick={(e) => { e.stopPropagation(); setExpanded(x => !x) }}
                sx={{
                  flexShrink: 0,
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.18s ease',
                  // K8 (2026-10-10): wie am Stummschalter darueber —
                  // zeigergebunden statt breitengebunden.
                  ...TOUCH_TARGET_COARSE_QUADRAT,
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {has_unread && (
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: urgencyColor, alignSelf: 'center', flexShrink: 0,
            }} />
          )}
        </Stack>
      </Box>
      {isMulti && (
        <Collapse in={expanded} timeout={220} unmountOnExit>
          <Box sx={{
            bgcolor: 'background.default',
            ml: SP.sm,
            borderLeft: '2px solid',
            borderLeftColor: 'primary.light',
          }}>
            {items.map(child => (
              <ChildRow
                key={child.id}
                notification={child}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  )
}

export default function NotificationDropdown({ onClose }) {
  const navigate = useNavigate()
  const { notifications, unreadCount, markSeen, markAllSeen } = useNotificationsContext()
  const [onlyUnread, setOnlyUnread] = useState(false)

  const sections = useMemo(
    () => groupAndSection(notifications, { onlyUnread }),
    [notifications, onlyUnread]
  )

  // Per-notification navigation: mark seen + deep-link + close popover.
  // Used by single-item groups (row click) and by every ChildRow inside an
  // expanded group. Multi-update groups expand inline rather than navigate.
  const handleNavigate = (notification) => {
    if (notification.seen_at == null) markSeen(notification.id)
    if (notification.deep_link) {
      navigate(notification.deep_link)
      onClose?.()
    }
  }

  const handleMute = async (notification) => {
    const muteBody = notification.dedup_key
      ? { dedup_key: notification.dedup_key, duration_hours: 24, reason: 'muted from bell' }
      : { trigger_key: notification.trigger_key, duration_hours: 24, reason: 'muted from bell' }
    try {
      await axios.post(`${API}/api/notify/mutes`, muteBody)
    } catch (e) { /* silent */ }
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: SP.sm, pb: SP.tight }}>
        <Typography sx={{ fontSize: FS.md, fontWeight: 700 }}>
          Notifications
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={markAllSeen}>
                <DoneAllIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open notification center">
            <IconButton size="small" onClick={() => { navigate('/notifications'); onClose?.() }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Status filter: All / Unread */}
      <Box sx={{ px: SP.sm, pb: SP.tight }}>
        <SegmentedSlider
          value={onlyUnread ? 'unread' : 'all'}
          onChange={(v) => setOnlyUnread(v === 'unread')}
          options={[
            { value: 'all', label: 'All' },
            { value: 'unread', label: 'Unread' },
          ]}
          size="small"
        />
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
        {sections.length === 0 ? (
          <Box sx={{ p: SP.lg, textAlign: 'center' }}>
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
              {onlyUnread ? 'No unread notifications.' : 'All clear ✓'}
            </Typography>
          </Box>
        ) : (
          sections.map(section => (
            <Box key={section.date_label}>
              <Typography
                component="h3"
                sx={{
                  fontSize: FS.xxs, fontWeight: 600, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  px: SP.sm, py: 0.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid', borderBottomColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  m: 0,
                }}
              >
                {section.date_label}
              </Typography>
              {section.groups.map(group => (
                <GroupRow
                  key={group.group_key}
                  group={group}
                  onNavigate={handleNavigate}
                  onMute={handleMute}
                />
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}
